import {
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

/**
 * Память админки между переходами внутри SPA.
 *
 * Из админки уходят на доску (`/board/:id?spectate=1`) и возвращаются кнопкой
 * «Назад». Страница при этом монтируется заново, и без памяти админ оказывался
 * на первой вкладке, наверху, с пустыми фильтрами. Здесь хранится последнее
 * состояние каждого блока; восстанавливается оно только при возврате по
 * истории (POP), обычный заход открывает админку с нуля.
 *
 * Хранится в модуле, а не в sessionStorage: списки бывают по сотне строк,
 * а после перезагрузки вкладки данные всё равно надо запрашивать заново.
 */
const cache = new Map<string, unknown>();

/** true — этот монтаж восстанавливает прошлое состояние (возврат «Назад»). */
export const AdminViewRestoreContext = createContext(false);

export function useAdminViewRestoring(): boolean {
  return useContext(AdminViewRestoreContext);
}

export function hasAdminView(key: string): boolean {
  return cache.has(key);
}

export function readAdminView<T>(key: string): T | undefined {
  return cache.get(key) as T | undefined;
}

export function writeAdminView(key: string, value: unknown): void {
  cache.set(key, value);
}

/**
 * useState, который запоминает значение под `key` и при восстановлении
 * стартует с запомненного. Ключ должен включать контур админки, иначе
 * школьная и внешняя админки перепутают состояние. `key = null` — обычный
 * useState без памяти.
 *
 * `restoring` передаёт сама страница админки: она стоит выше провайдера
 * контекста и не может прочитать его.
 */
export function useAdminViewState<T>(
  key: string | null,
  initial: T | (() => T),
  restoring?: boolean
): [T, Dispatch<SetStateAction<T>>] {
  const fromContext = useAdminViewRestoring();
  const restore = restoring ?? fromContext;
  const [value, setValue] = useState<T>(() => {
    if (key !== null && restore && cache.has(key)) return cache.get(key) as T;
    return typeof initial === "function" ? (initial as () => T)() : initial;
  });
  useEffect(() => {
    if (key !== null) cache.set(key, value);
  }, [key, value]);
  return [value, setValue];
}

const USER_SCROLL_EVENTS = ["wheel", "touchstart", "pointerdown", "keydown"] as const;

/**
 * Восстанавливает прокрутку контейнера после возврата «Назад».
 * Часть содержимого догружается асинхронно (карточка доски, сессии), поэтому
 * позицию подтягиваем несколько кадров подряд, пока высоты не хватит.
 * Любое действие пользователя останавливает попытки.
 *
 * `pendingTarget()` — позиция, до которой ещё не докрутили (null, если
 * докрутили или бросили): уйдя со страницы раньше, запоминаем её, а не
 * промежуточную.
 */
export function restoreScrollTop(
  el: HTMLElement,
  target: number
): { stop: () => void; pendingTarget: () => number | null } {
  if (!(target > 0)) return { stop: () => {}, pendingTarget: () => null };
  const deadline = performance.now() + 3000;
  let frame = 0;
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (frame) cancelAnimationFrame(frame);
    for (const ev of USER_SCROLL_EVENTS) el.removeEventListener(ev, stop);
  };
  for (const ev of USER_SCROLL_EVENTS) el.addEventListener(ev, stop, { passive: true });
  const step = () => {
    if (stopped) return;
    el.scrollTop = target;
    if (Math.abs(el.scrollTop - target) < 2 || performance.now() > deadline) {
      stop();
      return;
    }
    frame = requestAnimationFrame(step);
  };
  step();
  return { stop, pendingTarget: () => (stopped ? null : target) };
}
